import { IonButton, IonInput, IonItem } from '@ionic/react';
import { useFormik } from 'formik';
import i18next from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useMutateConfirmDeliveryReturn } from '../../../api/hooks/delivery/useMutateConfirmDeliveryReturn';
import { Loading } from '../../../components/atoms/Loading';
import { QrScanButton } from '../../../components/molecules/QrScanButton';
import { disableSubmitOnEnter } from '../../../config/utils/formUtils';
import { DeliveryStatusByQrUrl } from './DeliveryStatusByQrUrl';
import { ProviderInvoiceDoc } from './ProviderInvoiceDoc';

interface Props {
  mark?: string;
  qrUrl?: string;
}

const validationSchema = yup.object().shape({
  qrUrl: yup.string().trim().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
});

export const DeliveryConfirmReturnForm = ({ mark, qrUrl }: Props) => {
  const { t } = useTranslation();
  const { mutate, isLoading } = useMutateConfirmDeliveryReturn();
  const [committedQrUrl, setCommittedQrUrl] = useState<string | undefined>(qrUrl);

  const formik = useFormik({
    initialValues: {
      qrUrl: qrUrl ?? '',
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      mutate(
        { mark, qrUrl: values.qrUrl },
        {
          onSuccess: (data) => {
            if (data?.success) {
              setCommittedQrUrl(values.qrUrl);
              resetForm();
            }
          },
        },
      );
    },
  });

  return (
    <>
      {isLoading && <Loading global />}
      <div>
        <form onSubmit={formik.handleSubmit}>
          <div className="simpleBorder p-12">
            <div className="boldText5 margin-b-24 text-center">{t('DELIVERY.CONFIRM_RETURN_TITLE')}</div>

            <div className="flex-center-center-row-wrap gap-12 p-b-12">
              {!qrUrl && (
                <div>
                  <div className="p-b-12 p-l-6 flex-start-center-row-nowrap gap-6">
                    {t('DELIVERY.QR_TITLE')}
                    <QrScanButton onScan={(url) => formik.setFieldValue('qrUrl', url)} />
                  </div>
                  <IonItem
                    className={`width-300px height-50px border-radius-input g-select-border ${
                      formik.touched.qrUrl && formik.errors.qrUrl ? 'b-field-border-error' : ''
                    }`}
                    mode="md">
                    <IonInput
                      className="height-48px"
                      mode="md"
                      type="text"
                      name="qrUrl"
                      placeholder={t('DELIVERY.QR_PLACEHOLDER') + '...'}
                      onIonInput={(e: any) => formik.setFieldValue('qrUrl', e?.detail?.value)}
                      onIonBlur={() => {
                        formik.setFieldTouched('qrUrl', true);
                        setCommittedQrUrl(formik.values.qrUrl || undefined);
                      }}
                      value={formik.values.qrUrl}
                      onKeyPress={disableSubmitOnEnter}
                    />
                  </IonItem>
                </div>
              )}
            </div>
          </div>

          <div className="flex-center-center-column-nowrap m-t-30 width-100">
            <div className="max-width-460px">
              <IonButton
                mode="md"
                type="submit"
                disabled={!formik.isValid || isLoading}
                className="button-focused width-100">
                {t('DELIVERY.CONFIRM_RETURN_SUBMIT')}
              </IonButton>
            </div>
          </div>
        </form>
        <ProviderInvoiceDoc qrUrl={formik.values.qrUrl} />
        {/* When `mark` is already known, the parent screen already fetched and renders this same
            status/doc data via useDeliveryStatusByMark -- avoid a duplicate qr-status request */}
        {!mark && <DeliveryStatusByQrUrl qrUrl={committedQrUrl} />}
      </div>
    </>
  );
};
